<template>
  <td class="px-2">
    <v-row class="my-1 px-0">

      <!-- Count Field -->
      <v-col :cols="tprProTenants ? 3 : 4"
        class="pa-0 flex-grow-0" >
        <v-text-field
          v-if="!tprProTenants"
          :background-color="bgColor"
          :disabled="disabled"
          v-model.number="item.multiplier"
          dense
          outlined
          hide-details
          solo
          type="number"
          @change="$emit('multiplier-change', { item, value: item.multiplier })"
          @input="$emit('gp-recompute', item)">
        </v-text-field>
        <v-text-field 
          v-else
          :readonly="isReadOnlyUser"
          :disabled="disabled"
          :background-color="bgColor"
          :value="item.multiplier"
          dense
          outlined
          hide-details
          solo
          type="number"
          @input="$emit('handle-input', { item, value: $event, field: 'multiplier' })"
          @blur="$emit('multiplier-blur', { item })">
        </v-text-field>
      </v-col>

      <!-- @ Icon -->
      <v-icon size="20" class="px-2">mdi-at</v-icon>

      <!-- SRP Field -->
      <v-col class="pa-0 flex-grow-1">
        <v-tooltip :disabled="!showCompliantTooltip" top>
          <template v-slot:activator="{ on }">
            <v-text-field
              v-if="!tprProTenants"
              :readonly="hasSelections"
              :rules="srpRules"
              v-on="on"
              prefix="$"
              :background-color="bgColor"
              class="px-0"
              dense
              outlined
              hide-details
              solo
              step=".01"
              type="number"
              v-model.number="item.promotion_price"
              @input="$emit('gp-recompute', item)"
              @blur="$emit('srp-blur', { item, event: $event })"
              @change="$emit('srp-change', { item, value: item.promotion_price })"
              v-bind="{'data-row': index, 'data-field': 'srpInput'}"
              @keydown.enter="$emit('enter', { index, field: 'srpInput', event: $event })"
              :disabled="disabled">
            </v-text-field>
            <v-text-field
              v-else
              :readonly="hasSelections || isReadOnlyUser"
              :rules="srpRules"
              :value="item.promotion_price"
              @input="$emit('handle-input', { item, value: $event, field: 'promotion_price' })"
              @blur="$emit('srp-blur', { item, event: $event })"
              v-bind="{'data-row': index, 'data-field': 'srpInput'}"
              @keydown.enter="$emit('enter', { index, field: 'promotion_price', event: $event })"
              v-on="on"
              data-field="promotion_price"
              prefix="$"
              :background-color="bgColor"
              class="px-0"
              dense
              outlined
              hide-details
              solo
              step=".01"
              type="number"
              :disabled="disabled">
            </v-text-field>
          </template>
          <span>{{ nonComplianceTooltipText }}
          </span>
        </v-tooltip>
      </v-col>

      <!-- Linked Group Modal Button -->
      <v-col class="pa-0 flex-grow-0">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="tprProTenants"
              :style="{ visibility: item.linked_group != null && !isEditingDisabled ? 'visible' : 'hidden' }"
              :disabled="linkedGroupBtnDisabled || isReadOnlyUser"
              class="mt-5"
              v-on="on"
              x-small
              icon
              @click="$emit('show-linked-group', item)">
              <v-icon color="blue darken-2">mdi-alpha-l-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Apply SRP to ALL Linked Group items</span>
        </v-tooltip>
      </v-col>

      <!-- Reset Value Button -->
      <v-col class="pa-0 flex-grow-0">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
          <v-btn
            v-if="tprProTenants"
            @click="$emit('reset', item)"
            icon
            v-on="on"
            :style="{ visibility: isChanged ? 'visible' : 'hidden' }">
            <v-icon>mdi-refresh-circle</v-icon>
          </v-btn>
          </template>
          <span>Reset Values</span>
        </v-tooltip>
      </v-col>
    </v-row>
  </td>
</template>
<script>

import { userAccess } from '@/mixins/user-access'

export default {
  name: 'BatchSrpCell',
  props: {
    item: { type: Object, required: true },
    index: { type: Number, required: true },
    isEditingDisabled: { type: Boolean, default: false },
    srpExceedsBaseRetail: { type: Boolean, default: false },
    tprProTenants: { type: Boolean, default: false },
    hasSelections: { type: Boolean, default: false },
    srpRules: { type: Array, default: () => [] },
    isChanged: { type: Boolean, default: false }
  },
  mixins: [userAccess],
  computed: {
    disabled() {
      if (this.isEditingDisabled) return true

      return this.tprProTenants && this.item.excluded !== '' && !this.srpExceedsBaseRetail && !this.isChanged
    },
    isArgOrAwgTenant() {
      return this.$auth.tenant === 'awg' || this.$auth.tenant === 'alliance-retail-group'
    },
    srpCompliant() {
      // Mirror the parent's srpIsCompliant logic exactly.
      const item = this.item
      if (item.enforce_compliance == false) return true
      // item.variance is blank on store-group rows; the backend defaults it to the nickel tolerance, so mirror that.
      const variance = parseFloat(item.variance)
      const tolerance = Number.isNaN(variance) ? 0.05 : variance
      const base = this.isTprProRetailUser ? item.original_promotion_price : item.rules_promotion_price
      const complianceLimit = (parseFloat(base) + tolerance).toFixed(2) * item.multiplier
      return !(parseFloat(item.promotion_price) > complianceLimit)
    },
    bgColor() {
      if (this.tprProTenants && this.isTprProRetailUser && !this.srpCompliant) return 'red'
      if (this.tprProTenants && this.srpExceedsBaseRetail) return 'amber lighten-5'
      return this.isArgOrAwgTenant && this.item.version_id > 1 ? 'blue-grey lighten-3' : 'input'
    },
    showCompliantTooltip() {
      return (this.isTprProRetailUser && !this.srpCompliant) || this.hasSelections
    },
    nonComplianceTooltipText() {
      if (this.hasSelections) return 'Cannot edit SRP while items are selected'
      return `This item is out of compliance for the following promos: ${this.item.nonCompliantList}`
    },
    linkedGroupBtnDisabled() {
      return (!this.isTprProRetailUser && !this.srpCompliant) || (this.tprProTenants && this.item.excluded !== '')
    }
  }
}
</script>
